import React from 'react'
import { MaterialFieldProps } from './MaterialField'
import { Field, WrappedFieldProps } from 'redux-form'
import { useRegisterTab } from '../Tabs/TabItem'
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox'
import FormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import { useFieldError } from './hooks/useHelperText'
import mergeSx from '@carsys/ui/src/helpers/merge-sx'

export type StrippedCheckboxProps = Omit<CheckboxProps, 'onBlur' | 'onChange' | 'onFocus' | 'type'>

export interface ReduxCheckboxProps extends MaterialFieldProps<StrippedCheckboxProps>, Pick<FormControlLabelProps, 'label'> {}
interface RenderCheckboxProps extends CheckboxProps, WrappedFieldProps, Pick<FormControlLabelProps, 'label'> {}

const RenderCheckbox = (props: RenderCheckboxProps) => {
  const {
    onChange,
    input,
    input: { value, ...restInput },
    meta,
    label,
    color,
    className,
    sx,
    ...rest
  } = props
  const { isError, errorText } = useFieldError(props.meta)
  const handleOnchange = (e: React.ChangeEvent<HTMLInputElement>) => {
    input.onChange(e)
    onChange && onChange(e, e.target.checked)
  }

  const id = rest.id ?? rest.name ?? restInput.name

  return (
    <FormControl
      required
      error={isError}
      component='fieldset'
      className={className}
      sx={mergeSx(
        {
          '& .readOnly:hover, input:read-only': {
            cursor: 'pointer'
          }
        },
        sx
      )}
    >
      <FormControlLabel
        control={<Checkbox {...restInput} checked={!!value} onChange={handleOnchange} disableRipple color={color} {...rest} id={id} />}
        label={label}
      />
      <FormHelperText>{errorText}</FormHelperText>
    </FormControl>
  )
}

export const ReduxCheckbox: React.FC<ReduxCheckboxProps> = props => {
  // we really need a way to split all reduxFieldProps and materialField props.
  // take care not to pass the onChange directly to the props, it will override the redux field handler
  const { validate, name, placeholder, label, onChange, ...rest } = props
  useRegisterTab(name)

  return (
    <Field
      props={rest}
      placeholder={placeholder}
      validate={validate ? [props.validate] : undefined}
      name={name}
      label={label}
      onChange={onChange}
      component={RenderCheckbox}
    />
  )
}

ReduxCheckbox.displayName = 'ReduxCheckbox'

export default React.memo(ReduxCheckbox)
